import { SetType, TrainingSetItemStatus } from '../../shared/constants';
import { IExercise } from '../../types/interfaces/exercise';
import { IExerciseOption } from '../../types/interfaces/exercise-option';
import { IProgramItem } from '../../types/interfaces/program-item';
import { IProgramSet } from '../../types/interfaces/program-set';
import { IProgramSetItem } from '../../types/interfaces/program-set-item';
import { IProgramSetItemOption } from '../../types/interfaces/program-set-item-option';
import { ITraining } from '../../types/interfaces/training';
import { ITrainingSet } from '../../types/interfaces/training-set';
import { ITrainingSetItem } from '../../types/interfaces/training-set-item';
import { ITrainingSetItemOption } from '../../types/interfaces/training-set-item-option';
import { orderBy } from '../collections/array-utils';

export const DEFAULT_SET_ITEM_REST_SEC = 120;

/** Generates a unique id for new unsaved set/set item. Stable for list keys across reorder/delete. */
export function generateTemporaryId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Stable key for set items: real id or temporaryId. Use for React list keys. */
export function getSetItemKey(item: {
  id?: string;
  temporaryId?: string;
  orderNumber?: number;
}): string {
  return (
    item?.id ?? item?.temporaryId ?? `legacy-item-${item?.orderNumber ?? 0}`
  );
}

/** Stable key for sets (program/training): real id or temporaryId. Use for React list keys. */
export function getSetKey(set: {
  id?: string;
  temporaryId?: string;
  setNumber?: number;
  orderNumber?: number;
}): string {
  return (
    set?.id ??
    set?.temporaryId ??
    `legacy-set-${set?.setNumber ?? 0}-${set?.orderNumber ?? 0}`
  );
}

function areNullableValuesEqual(a: any, b: any): boolean {
  if (a === b) return true;
  return (a === null || a === undefined) && (b === null || b === undefined);
}

export function orderSets<T extends IProgramSet[] | ITrainingSet[]>(
  sets: T,
): T {
  if (!sets || !sets?.length) return [] as T;

  const firstSet = sets[0];

  if ('trainingSetItems' in firstSet) {
    return orderBy<ITrainingSet>(sets as ITrainingSet[], [
      'setNumber',
      'orderNumber',
    ]).map((set: ITrainingSet) => ({
      ...set,
      trainingSetItems: orderBy(set.trainingSetItems || [], ['orderNumber']),
    })) as T;
  } else if ('programSetItems' in firstSet) {
    return orderBy<IProgramSet>(sets as IProgramSet[], [
      'setNumber',
      'orderNumber',
    ]).map((set: IProgramSet) => ({
      ...set,
      programSetItems: orderBy(set.programSetItems || [], ['orderNumber']),
    })) as T;
  }

  return sets;
}

export function changeSetNumber(
  sets: ITrainingSet[] | IProgramSet[],
  setNumber: number,
  direction: 'up' | 'down',
) {
  const updatedSets = sets.map((set) => {
    if (set.setNumber === setNumber) {
      return {
        ...set,
        setNumber: set.setNumber + (direction === 'up' ? -1 : 1),
      };
    } else if (direction === 'up' && set.setNumber === setNumber - 1) {
      return {
        ...set,
        setNumber: set.setNumber + 1,
      };
    } else if (direction === 'down' && set.setNumber === setNumber + 1) {
      return {
        ...set,
        setNumber: set.setNumber - 1,
      };
    }

    return set;
  });

  return orderSets(updatedSets);
}

export function changeSetItemOrder<
  T extends ITrainingSetItem[] | IProgramSetItem[],
>(setItems: T, oldOrderNumber: number, newOrderNumber: number): T {
  const orderedItems: T = setItems.map((item) => {
    if (item.orderNumber === oldOrderNumber) {
      return { ...item, orderNumber: newOrderNumber };
    }
    if (
      item.orderNumber > oldOrderNumber &&
      item.orderNumber <= newOrderNumber
    ) {
      return { ...item, orderNumber: item.orderNumber - 1 };
    }
    if (
      item.orderNumber < oldOrderNumber &&
      item.orderNumber >= newOrderNumber
    ) {
      return { ...item, orderNumber: item.orderNumber + 1 };
    }
    return item;
  }) as T;

  orderedItems.sort((a, b) => a.orderNumber - b.orderNumber);

  return orderedItems;
}

export function orderTraining(
  training: ITraining,
  exercises?: IExercise[],
  exerciseOptions?: IExerciseOption[],
): ITraining {
  return {
    ...training,
    trainingSets: orderBy(
      training?.trainingSets?.map((set: ITrainingSet) =>
        orderTrainingSet(
          set,
          exercises?.find((e) => e.id === set.exerciseId),
          exerciseOptions,
        ),
      ),
      ['setNumber', 'orderNumber'],
    ),
  };
}

export function orderTrainingSet(
  trainingSet: ITrainingSet,
  exercise: IExercise,
  exerciseOptions?: IExerciseOption[],
): ITrainingSet {
  const orderedExercise = orderExerciseOptions(
    exercise || trainingSet.exercise,
  );

  return {
    ...trainingSet,
    exercise: orderedExercise,
    trainingSetItems: orderBy(
      trainingSet.trainingSetItems.map((item) => {
        return {
          ...item,
          trainingSetItemOptions:
            exerciseOptions || exercise?.options
              ? orderOptions(
                  item.trainingSetItemOptions,
                  exerciseOptions || exercise?.options,
                )
              : item.trainingSetItemOptions,
        };
      }),
      'orderNumber',
    ),
  };
}

export function orderProgramItem(
  program: IProgramItem,
  exercises?: IExercise[],
  exerciseOptions?: IExerciseOption[],
): IProgramItem {
  return {
    ...program,
    programSets: orderBy(
      program?.programSets?.map((set: IProgramSet) =>
        orderProgramSet(
          set,
          exercises?.find((e) => e.id === set.exerciseId),
          exerciseOptions,
        ),
      ),
      ['setNumber', 'orderNumber'],
    ),
  };
}

export function orderProgramSet(
  programSet: IProgramSet,
  exercise: IExercise,
  exerciseOptions?: IExerciseOption[],
): IProgramSet {
  const orderedExercise = orderExerciseOptions(exercise || programSet.exercise);

  return {
    ...programSet,
    exercise: orderedExercise,
    programSetItems: orderBy(
      programSet.programSetItems.map((item) => {
        return {
          ...item,
          programSetItemOptions:
            exerciseOptions || exercise?.options
              ? orderOptions(
                  item.programSetItemOptions,
                  exerciseOptions || exercise?.options,
                )
              : item.programSetItemOptions,
        };
      }),
      'orderNumber',
    ),
  };
}

export function orderOptions<
  T extends IProgramSetItemOption | ITrainingSetItemOption,
>(setItemOptions: T[], exerciseOptions: IExerciseOption[]): T[] {
  const orderedOptions =
    setItemOptions && exerciseOptions ? [...setItemOptions] : [];

  const optionById = new Map(
    (exerciseOptions || []).map((option) => [option.id, option]),
  );

  return orderedOptions
    .map((option, index) => ({
      option,
      index,
      priorityOrder: optionById.get(option.optionId)?.priorityOrder,
    }))
    .sort((a, b) => {
      const aHasPriority = typeof a.priorityOrder === 'number';
      const bHasPriority = typeof b.priorityOrder === 'number';

      if (aHasPriority && bHasPriority && a.priorityOrder !== b.priorityOrder) {
        return a.priorityOrder - b.priorityOrder;
      }

      if (aHasPriority !== bHasPriority) {
        return aHasPriority ? -1 : 1;
      }

      const optionIdCompare = (a.option.optionId || '').localeCompare(
        b.option.optionId || '',
      );

      if (optionIdCompare !== 0) {
        return optionIdCompare;
      }

      return a.index - b.index;
    })
    .map(({ option }) => option);
}

export function getEmptyProgramSetItem(
  orderNumber: number,
  options: IExerciseOption[] = [],
  restSec: number = DEFAULT_SET_ITEM_REST_SEC,
): IProgramSetItem {
  const ops = [...options];

  ops.sort((option1, option2) => option1.priorityOrder - option2.priorityOrder);

  const programSetItemOptions: IProgramSetItemOption[] = ops.map((option) => ({
    optionId: option.id,
    value: null,
    value2: null,
  }));

  return {
    orderNumber,
    type: SetType.normal,
    rest: restSec,
    sets: 1,
    programSetItemOptions,
    temporaryId: generateTemporaryId(),
  };
}

export function copyProgramSetItemToNext(
  programSetItem: IProgramSetItem,
): IProgramSetItem {
  const programSetItemOptions: IProgramSetItemOption[] =
    programSetItem.programSetItemOptions?.map((option) => ({
      optionId: option.optionId,
      value: option.value,
      value2: option.value2,
    }));

  return {
    orderNumber: programSetItem.orderNumber + 1,
    type: programSetItem.type,
    rest: programSetItem.rest,
    sets: programSetItem.sets,
    programSetItemOptions,
    temporaryId: generateTemporaryId(),
  };
}

export function getEmptyTrainingSetItem(
  orderNumber: number,
  options: IExerciseOption[] = [],
  restSec: number = DEFAULT_SET_ITEM_REST_SEC,
): ITrainingSetItem {
  const ops = [...options];

  ops.sort((option1, option2) => option1.priorityOrder - option2.priorityOrder);

  const trainingSetItemOptions: ITrainingSetItemOption[] = ops.map(
    (option) => ({
      optionId: option.id,
      value: null,
      value2: null,
      programValue: null,
      programValue2: null,
    }),
  );

  return {
    orderNumber,
    status: TrainingSetItemStatus.notStarted,
    type: SetType.normal,
    rest: restSec,
    trainingSetItemOptions,
    temporaryId: generateTemporaryId(),
  };
}

export function getEmptySetItem(
  mode: 'program' | 'training',
  orderNumber: number,
  options: IExerciseOption[] = [],
  restSec: number = DEFAULT_SET_ITEM_REST_SEC,
): ITrainingSetItem | IProgramSetItem {
  if (mode === 'program') {
    return getEmptyProgramSetItem(
      orderNumber,
      options,
      restSec,
    ) as IProgramSetItem;
  } else if (mode === 'training') {
    return getEmptyTrainingSetItem(
      orderNumber,
      options,
      restSec,
    ) as ITrainingSetItem;
  }
}

export function copyTrainingSetItemToNext(
  trainingSetItem: ITrainingSetItem,
): ITrainingSetItem {
  const trainingSetItemOptions: ITrainingSetItemOption[] =
    trainingSetItem.trainingSetItemOptions?.map((option) => ({
      optionId: option.optionId,
      value: option.value,
      value2: option.value2,
      programValue: option.programValue,
      programValue2: option.programValue2,
    }));

  return {
    orderNumber: trainingSetItem.orderNumber + 1,
    status: TrainingSetItemStatus.notStarted,
    type: trainingSetItem.type,
    rest: trainingSetItem.rest,
    trainingSetItemOptions,
    temporaryId: generateTemporaryId(),
  };
}

export function orderExerciseOptions(exercise: IExercise): IExercise {
  if (exercise?.options?.length > 1) {
    const ops = [...exercise.options];

    ops.sort(
      (option1, option2) => option1.priorityOrder - option2.priorityOrder,
    );

    return { ...exercise, options: ops };
  }

  return exercise;
}

export function getEmptyProgramSet(
  setNumber: number,
  orderNumber: number,
  options: {
    haveOwner: boolean;
    programItemId?: string;
    ownerId?: string;
    defaultItemRestSec?: number;
  } = {
    haveOwner: false,
    programItemId: null,
    ownerId: null,
  },
): IProgramSet {
  const itemRest = options.defaultItemRestSec ?? DEFAULT_SET_ITEM_REST_SEC;
  const emptyProgramSet: IProgramSet = {
    exerciseId: null,
    orderNumber: orderNumber,
    setNumber: setNumber,
    programSetItems: [getEmptyProgramSetItem(0, [], itemRest)],
    temporaryId: generateTemporaryId(),
  };

  if (options.haveOwner) {
    emptyProgramSet.ownerId = options.ownerId;
  } else {
    emptyProgramSet.programItemId = options.programItemId;
  }

  return emptyProgramSet;
}

export function getEmptyTrainingSet(
  setNumber: number,
  orderNumber: number,
  trainingId: string,
  defaultItemRestSec: number = DEFAULT_SET_ITEM_REST_SEC,
): ITrainingSet {
  const emptyTrainingSet: ITrainingSet = {
    exerciseId: null,
    orderNumber: orderNumber,
    setNumber: setNumber,
    trainingId,
    trainingSetItems: [getEmptyTrainingSetItem(0, [], defaultItemRestSec)],
    temporaryId: generateTemporaryId(),
  };

  return emptyTrainingSet;
}

export function getSetFromSavedProgramSet(
  mode: 'program' | 'training',
  programSet: IProgramSet,
  aditionalOptions: {
    setNumber: number;
    orderNumber: number;
    trainingId?: string;
    programItemId?: string;
    ownerId?: string;
  },
): IProgramSet | ITrainingSet {
  if (mode === 'program') {
    const { id, ...newProgramSet } = programSet;

    newProgramSet.programSetItems = programSet.programSetItems.map(
      (item: IProgramSetItem) => {
        const { id: itemId, ...newItem } = item;
        return {
          ...newItem,
          temporaryId: generateTemporaryId(),
          programSetItemOptions: item.programSetItemOptions.map((option) => {
            const { id: optId, ...newOption } = option;

            return newOption;
          }),
        };
      },
    );
    newProgramSet.setNumber = aditionalOptions.setNumber;
    newProgramSet.orderNumber = aditionalOptions.orderNumber;
    newProgramSet.temporaryId = generateTemporaryId();

    if (aditionalOptions?.programItemId) {
      newProgramSet.programItemId = aditionalOptions.programItemId;
    }

    if (aditionalOptions?.ownerId) {
      newProgramSet.ownerId = aditionalOptions.ownerId;
    }

    return newProgramSet;
  } else if (mode === 'training') {
    let itemOrderNumber = 0;

    return {
      exerciseId: programSet.exerciseId,
      exercise: programSet.exercise,
      setNumber: aditionalOptions.setNumber,
      orderNumber: aditionalOptions.orderNumber,
      trainingId: aditionalOptions.trainingId,
      temporaryId: generateTemporaryId(),
      trainingSetItems: programSet.programSetItems.flatMap(
        (item: IProgramSetItem): ITrainingSetItem[] => {
          const result = [];

          for (let i = 0; i < item.sets; i++) {
            result.push({
              orderNumber: itemOrderNumber,
              rest: item.rest,
              status: TrainingSetItemStatus.notStarted,
              type: item.type || SetType.normal,
              temporaryId: generateTemporaryId(),
              trainingSetItemOptions: item.programSetItemOptions.map(
                (setItemOption) => ({
                  optionId: setItemOption.optionId,
                  value: null,
                  value2: null,
                  programValue: setItemOption.value,
                  programValue2: setItemOption.value2,
                }),
              ),
            });
            itemOrderNumber++;
          }

          return result;
        },
      ),
    } as ITrainingSet;
  }
}

export function getProgramSetFromTrainingSet(
  trainingSet: ITrainingSet,
): IProgramSet {
  const programSetItems = [];
  let currentSetItem = null;

  trainingSet.trainingSetItems.forEach((item) => {
    if (!currentSetItem) {
      currentSetItem = {
        sets: 1,
        rest: item.rest,
        type: item.type,
        orderNumber: 0,
        temporaryId: generateTemporaryId(),
        programSetItemOptions: item.trainingSetItemOptions.map((option) => {
          return {
            optionId: option.optionId,
            value: option.value,
            value2: option.value2,
          };
        }),
      };
    } else {
      if (
        currentSetItem.type === item.type &&
        currentSetItem.rest === item.rest &&
        currentSetItem.programSetItemOptions.length ===
          item.trainingSetItemOptions.length &&
        currentSetItem.programSetItemOptions.every((option) =>
          item.trainingSetItemOptions.some(
            (iOption) =>
              iOption.optionId === option.optionId &&
              areNullableValuesEqual(iOption.value, option.value) &&
              areNullableValuesEqual(iOption.value2, option.value2),
          ),
        )
      ) {
        currentSetItem.sets++;
      } else {
        programSetItems.push(currentSetItem);
        currentSetItem = {
          sets: 1,
          rest: item.rest,
          type: item.type,
          orderNumber: currentSetItem.orderNumber + 1,
          temporaryId: generateTemporaryId(),
          programSetItemOptions: item.trainingSetItemOptions.map((option) => {
            return {
              optionId: option.optionId,
              value: option.value || option.programValue,
              value2: option.value2 || option.programValue2,
            };
          }),
        } as IProgramSetItem;
      }
    }
  });
  programSetItems.push(currentSetItem);

  return {
    exerciseId: trainingSet.exerciseId,
    exercise: trainingSet.exercise,
    setNumber: trainingSet.setNumber,
    orderNumber: trainingSet.orderNumber,
    temporaryId: generateTemporaryId(),
    programSetItems,
  };
}

export function getEmptyProgramSetFromProgramSet(
  set: IProgramSet,
): IProgramSet {
  return {
    exerciseId: set.exerciseId,
    exercise: set.exercise,
    setNumber: set.setNumber,
    orderNumber: set.orderNumber,
    title: set.title,
    ownerId: set.ownerId,
    temporaryId: generateTemporaryId(),
    programSetItems: set.programSetItems.map((item) => {
      const { id: itemId, ...newItem } = item;

      return {
        ...newItem,
        temporaryId: generateTemporaryId(),
        programSetItemOptions: newItem.programSetItemOptions.map((option) => {
          const { id: optId, ...newOption } = option;

          return newOption;
        }),
      };
    }),
  };
}
